/**
 * Xero Integration Page
 * Connect, sync, and manage Xero accounting software integration
 */

import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  getConnectionStatus,
  connect,
  disconnect,
  triggerSync,
  getSyncStatus,
  getContacts,
  getClientMappings,
  getSuggestedMappings,
  mapClient,
  XeroConnectionStatus,
  XeroContact,
  ClientMapping,
  SuggestedMapping,
} from '@/services/api/xero';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Link2,
  Unlink,
  RefreshCw,
  Loader2,
  CheckCircle,
  XCircle,
  Clock,
  Building2,
  Users,
  FileText,
  ArrowRight,
  Sparkles,
  Check,
  X,
} from 'lucide-react';
import { toast } from 'sonner';
import { useSearchParams } from 'react-router-dom';
import { PageHeader } from '@/components/shared/PageHeader';

export function XeroIntegrationPage() {
  const queryClient = useQueryClient();
  const [searchParams] = useSearchParams();

  // Show toast on return from OAuth
  useEffect(() => {
    if (searchParams.get('xero_connected') === 'true') {
      toast.success('Xero connected successfully!');
    }
    if (searchParams.get('error')) {
      toast.error(`Xero connection failed: ${searchParams.get('error')}`);
    }
  }, [searchParams]);

  const { data: status, isLoading: statusLoading } = useQuery({
    queryKey: ['xero-status'],
    queryFn: getConnectionStatus,
  });

  const { data: syncLogs = [] } = useQuery({
    queryKey: ['xero-sync-status'],
    queryFn: getSyncStatus,
    enabled: status?.connected === true,
    refetchInterval: status?.connected ? 10000 : false, // Poll during connection
  });

  const connectMutation = useMutation({
    mutationFn: () => connect('/panel/integrations/xero'),
    onSuccess: (data) => {
      window.location.href = data.authUrl;
    },
    onError: () => toast.error('Failed to initiate Xero connection'),
  });

  const disconnectMutation = useMutation({
    mutationFn: (xeroTenantId?: string) => disconnect(xeroTenantId),
    onSuccess: (_, xeroTenantId) => {
      queryClient.invalidateQueries({ queryKey: ['xero-status'] });
      toast.success(xeroTenantId ? 'Organization disconnected' : 'All Xero connections removed');
    },
    onError: () => toast.error('Failed to disconnect Xero'),
  });

  const syncMutation = useMutation({
    mutationFn: (syncType?: string) => triggerSync(undefined, syncType),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['xero-sync-status'] });
      toast.success('Sync started');
    },
    onError: () => toast.error('Failed to start sync'),
  });

  if (statusLoading) {
    return (
      <div className="flex justify-center py-16">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <PageHeader
        icon={Link2}
        title="Xero Integration"
        description="Connect your Xero accounting software to sync financial data"
      />


      {/* Connection Card */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Link2 className="h-5 w-5" />
            Connection Status
          </CardTitle>
          <CardDescription>
            Manage your Xero accounting software connection
          </CardDescription>
        </CardHeader>
        <CardContent>
          {status?.connected ? (
            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <CheckCircle className="h-5 w-5 text-green-500" />
                <span className="font-medium">Connected to Xero</span>
                <Badge variant={status.status === 'active' ? 'default' : 'destructive'}>
                  {status.status}
                </Badge>
              </div>

              {status.connectedBy && (
                <p className="text-sm text-muted-foreground">
                  Connected by: {status.connectedBy}
                </p>
              )}

              {status.organizations.length > 0 && (
                <div className="space-y-2">
                  <p className="text-sm font-medium">Connected Organizations ({status.organizations.length}):</p>
                  {status.organizations.map((org) => (
                    <div key={org.xero_tenant_id} className="flex items-center gap-2 rounded-md border p-3">
                      <Building2 className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                      <span className="font-medium truncate">{org.xero_tenant_name}</span>
                      <Badge variant="outline" className="ml-auto flex-shrink-0">
                        {org.last_sync_status || 'never synced'}
                      </Badge>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-7 px-2 text-xs text-destructive hover:text-destructive flex-shrink-0"
                        onClick={() => disconnectMutation.mutate(org.xero_tenant_id)}
                        disabled={disconnectMutation.isPending}
                      >
                        <X className="h-3 w-3" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}

              {status.errorMessage && (
                <div className="rounded-md bg-red-50 p-3 text-sm text-red-700 dark:bg-red-900/20 dark:text-red-400">
                  {status.errorMessage}
                </div>
              )}

              <div className="rounded-md bg-blue-50 p-3 text-sm text-blue-800 dark:bg-blue-900/20 dark:text-blue-300">
                <strong>Tip:</strong> Click "Add Organization" to connect more Xero orgs.
                On the Xero consent screen, select <strong>all</strong> organizations you want
                connected (including existing ones), as Xero updates all connections each time.
                You can disconnect individual orgs using the <strong>X</strong> button.
              </div>

              <div className="flex gap-2">
                <Button
                  variant="outline"
                  onClick={() => connectMutation.mutate()}
                  disabled={connectMutation.isPending}
                >
                  {connectMutation.isPending ? (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  ) : (
                    <Link2 className="mr-2 h-4 w-4" />
                  )}
                  Add Organization
                </Button>
                <Button
                  variant="destructive"
                  onClick={() => disconnectMutation.mutate(undefined)}
                  disabled={disconnectMutation.isPending}
                >
                  {disconnectMutation.isPending ? (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  ) : (
                    <Unlink className="mr-2 h-4 w-4" />
                  )}
                  Disconnect All
                </Button>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <XCircle className="h-5 w-5 text-muted-foreground" />
                <span>Not connected</span>
              </div>
              <p className="text-sm text-muted-foreground">
                Connect your Xero account to sync financial reports, invoices, bills, and bank transactions.
                This is a read-only connection - RoniDoc cannot modify your Xero data.
              </p>
              <Button
                onClick={() => connectMutation.mutate()}
                disabled={connectMutation.isPending}
              >
                {connectMutation.isPending ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <Link2 className="mr-2 h-4 w-4" />
                )}
                Connect to Xero
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Sync Card - only show when connected */}
      {status?.connected && (
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <RefreshCw className="h-5 w-5" />
                  Data Sync
                </CardTitle>
                <CardDescription>Sync financial data from Xero</CardDescription>
              </div>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => syncMutation.mutate('contacts')}
                  disabled={syncMutation.isPending}
                >
                  <Users className="mr-1 h-3 w-3" />
                  Contacts
                </Button>
                <Button
                  onClick={() => syncMutation.mutate('all')}
                  disabled={syncMutation.isPending}
                >
                  {syncMutation.isPending ? (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  ) : (
                    <RefreshCw className="mr-2 h-4 w-4" />
                  )}
                  Sync All
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {syncLogs.length === 0 ? (
              <p className="text-center py-4 text-muted-foreground">
                No sync history yet. Click "Sync All" to start your first sync.
              </p>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Type</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Items</TableHead>
                    <TableHead>Duration</TableHead>
                    <TableHead>Started</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {syncLogs.slice(0, 10).map((log) => (
                    <TableRow key={log.id}>
                      <TableCell className="font-medium capitalize">{log.sync_type}</TableCell>
                      <TableCell>
                        <Badge
                          variant={
                            log.status === 'completed' ? 'default' :
                            log.status === 'in_progress' ? 'outline' :
                            'destructive'
                          }
                        >
                          {log.status === 'in_progress' && <Loader2 className="mr-1 h-3 w-3 animate-spin" />}
                          {log.status}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        {log.items_processed} processed
                        {log.items_failed > 0 && (
                          <span className="text-red-500 ml-1">({log.items_failed} failed)</span>
                        )}
                      </TableCell>
                      <TableCell>
                        {log.duration_ms
                          ? `${(log.duration_ms / 1000).toFixed(1)}s`
                          : <Clock className="h-3 w-3 text-muted-foreground" />
                        }
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        {new Date(log.started_at).toLocaleString()}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      )}

      {/* Client Mapping Card - only show when connected */}
      {status?.connected && <ClientMappingCard />}
    </div>
  );
}

function ClientMappingCard() {
  const queryClient = useQueryClient();
  const [selectedMapping, setSelectedMapping] = useState<Record<string, string>>({});
  const [suggestions, setSuggestions] = useState<SuggestedMapping[]>([]);
  const [dismissedSuggestions, setDismissedSuggestions] = useState<Set<string>>(new Set());

  const { data: mappings = [], isLoading: mappingsLoading } = useQuery({
    queryKey: ['xero-client-mappings'],
    queryFn: getClientMappings,
  });

  const { data: contactsData } = useQuery({
    queryKey: ['xero-contacts'],
    queryFn: getContacts,
  });

  const contacts = contactsData?.contacts || [];

  const suggestMutation = useMutation({
    mutationFn: getSuggestedMappings,
    onSuccess: (data) => {
      setSuggestions(data);
      setDismissedSuggestions(new Set());
      if (data.length === 0) {
        toast.info('No matching contacts found');
      } else {
        toast.success(`Found ${data.length} suggested match${data.length > 1 ? 'es' : ''}`);
      }
    },
    onError: () => toast.error('Failed to find suggestions'),
  });

  const mapMutation = useMutation({
    mutationFn: ({ clientId, contactId, contactName }: { clientId: string; contactId: string | null; contactName?: string }) =>
      mapClient(clientId, contactId, contactName),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['xero-client-mappings'] });
      toast.success('Client mapping updated');
    },
    onError: () => toast.error('Failed to update mapping'),
  });

  const acceptSuggestion = (suggestion: SuggestedMapping) => {
    mapMutation.mutate({
      clientId: suggestion.clientId,
      contactId: suggestion.suggestedContact.ContactID,
      contactName: suggestion.suggestedContact.Name,
    });
    setSuggestions(prev => prev.filter(s => s.clientId !== suggestion.clientId));
  };

  const dismissSuggestion = (clientId: string) => {
    setDismissedSuggestions(prev => new Set(prev).add(clientId));
  };

  const acceptAllHighConfidence = () => {
    const highConfidence = activeSuggestions.filter(s => s.confidence === 'high');
    for (const s of highConfidence) {
      mapMutation.mutate({
        clientId: s.clientId,
        contactId: s.suggestedContact.ContactID,
        contactName: s.suggestedContact.Name,
      });
    }
    setSuggestions(prev => prev.filter(s => s.confidence !== 'high'));
  };

  const getSuggestionForClient = (clientId: string): SuggestedMapping | undefined => {
    if (dismissedSuggestions.has(clientId)) return undefined;
    return suggestions.find(s => s.clientId === clientId);
  };

  const activeSuggestions = suggestions.filter(s => !dismissedSuggestions.has(s.clientId));
  const highConfidenceCount = activeSuggestions.filter(s => s.confidence === 'high').length;
  const unmappedCount = mappings.filter(m => !m.xero_contact_id).length;

  const confidenceBadge = (suggestion: SuggestedMapping) => {
    const { confidence, matchReason } = suggestion;
    const label = matchReason.split(':')[0].replace(' match', '').replace(' similarity', '').trim();
    switch (confidence) {
      case 'high':
        return <Badge variant="default" className="text-[10px]">{label} Match</Badge>;
      case 'medium':
        return <Badge variant="secondary" className="text-[10px]">{label} Match</Badge>;
      case 'low':
        return <Badge variant="outline" className="text-[10px]">{label} Match</Badge>;
    }
  };

  if (mappingsLoading) return null;

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5" />
              Client Mapping
            </CardTitle>
            <CardDescription>
              Map your local clients to Xero contacts for automatic data association
            </CardDescription>
          </div>
          {unmappedCount > 0 && (
            <div className="flex gap-2">
              {highConfidenceCount > 0 && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={acceptAllHighConfidence}
                  disabled={mapMutation.isPending}
                >
                  <Check className="mr-1 h-3 w-3" />
                  Accept All ABN Matches ({highConfidenceCount})
                </Button>
              )}
              <Button
                variant="outline"
                size="sm"
                onClick={() => suggestMutation.mutate()}
                disabled={suggestMutation.isPending}
              >
                {suggestMutation.isPending ? (
                  <Loader2 className="mr-1 h-3 w-3 animate-spin" />
                ) : (
                  <Sparkles className="mr-1 h-3 w-3" />
                )}
                Suggest Matches
              </Button>
            </div>
          )}
        </div>
      </CardHeader>
      <CardContent>
        {mappings.length === 0 ? (
          <p className="text-center py-4 text-muted-foreground">
            No clients found. Create clients first, then sync contacts from Xero.
          </p>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Client</TableHead>
                <TableHead><ArrowRight className="h-4 w-4" /></TableHead>
                <TableHead>Xero Contact</TableHead>
                <TableHead className="w-[100px]"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {mappings.map((client) => {
                const suggestion = !client.xero_contact_id ? getSuggestionForClient(client.id) : undefined;

                return (
                  <TableRow key={client.id}>
                    <TableCell>
                      <div>
                        <span className="font-medium">{client.display_name}</span>
                        <div className="text-xs text-muted-foreground flex flex-wrap gap-x-3 mt-0.5">
                          <span>{client.client_code}</span>
                          {client.abn && <span>ABN: {client.abn}</span>}
                          {client.email && <span>{client.email}</span>}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      {client.xero_contact_id ? (
                        <CheckCircle className="h-4 w-4 text-green-500" />
                      ) : suggestion ? (
                        <Sparkles className="h-4 w-4 text-amber-500" />
                      ) : (
                        <XCircle className="h-4 w-4 text-muted-foreground" />
                      )}
                    </TableCell>
                    <TableCell>
                      {client.xero_contact_id ? (
                        <span>{client.xero_contact_name}</span>
                      ) : suggestion ? (
                        <div>
                          <div className="flex items-center gap-2">
                            <span className="font-medium">{suggestion.suggestedContact.Name}</span>
                            {confidenceBadge(suggestion)}
                          </div>
                          <div className="text-xs text-muted-foreground flex flex-wrap gap-x-3 mt-0.5">
                            {suggestion.suggestedContact.TaxNumber && <span>ABN: {suggestion.suggestedContact.TaxNumber}</span>}
                            {suggestion.suggestedContact.EmailAddress && <span>{suggestion.suggestedContact.EmailAddress}</span>}
                            <span className="italic">{suggestion.matchReason}</span>
                          </div>
                        </div>
                      ) : (
                        <Select
                          value={selectedMapping[client.id] || ''}
                          onValueChange={(value) => {
                            setSelectedMapping(prev => ({ ...prev, [client.id]: value }));
                            const contact = contacts.find(c => c.ContactID === value);
                            mapMutation.mutate({
                              clientId: client.id,
                              contactId: value,
                              contactName: contact?.Name,
                            });
                          }}
                        >
                          <SelectTrigger className="w-[250px]">
                            <SelectValue placeholder="Select Xero contact..." />
                          </SelectTrigger>
                          <SelectContent>
                            {contacts.map((contact) => (
                              <SelectItem key={contact.ContactID} value={contact.ContactID}>
                                <div className="flex items-center gap-2">
                                  <span>{contact.Name}</span>
                                  {contact.TaxNumber && <span className="text-xs text-muted-foreground">ABN: {contact.TaxNumber}</span>}
                                </div>
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      )}
                    </TableCell>
                    <TableCell>
                      {client.xero_contact_id ? (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => mapMutation.mutate({
                            clientId: client.id,
                            contactId: null,
                          })}
                        >
                          <Unlink className="h-3 w-3" />
                        </Button>
                      ) : suggestion ? (
                        <div className="flex gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-green-600 hover:text-green-700 hover:bg-green-50"
                            onClick={() => acceptSuggestion(suggestion)}
                            disabled={mapMutation.isPending}
                          >
                            <Check className="h-3 w-3" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-muted-foreground hover:text-destructive"
                            onClick={() => dismissSuggestion(client.id)}
                          >
                            <X className="h-3 w-3" />
                          </Button>
                        </div>
                      ) : null}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
}
